import { useMutation } from "@apollo/client";
import { Box, Collapse, Container, Text, useToast } from "@chakra-ui/react";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { connectModal, InjectedProps } from "redux-modal";
import { api } from "src/api";
import { BaseAccountFields } from "src/api/fragments";
import {
  AccountTypeEnum,
  ImportTypeEnum,
  MutationCreateAccountArgs,
  MutationRefreshApiKeyArgs,
} from "src/api/generated/types";
import HelpLink from "src/components/HelpLink";
import { Modal } from "src/components/Modal";
import { MyToast } from "src/components/MyToast";
import { Button, DatePicker, Info } from "src/components/styled";
import { Input } from "src/components/styled/Form/Input";
import { Touchable } from "src/components/Touchable";
import { useClientById, useMyToast } from "src/hooks";
import { useIsLargeScreen } from "src/hooks/useScreenSize";
import { useTheme } from "src/hooks/useTheme";
import Helpers from "src/utils/helpers";
import { IntegrationProviderInfo } from "../AccountModal/types";
import { WalletHeaderImage } from "../WalletAccountModal/WalletHeaderImage";

type Props = InjectedProps & {
  provider: string;
  accountId?: string;
  onSuccess: (acct?: BaseAccountFields) => void;
  integration: IntegrationProviderInfo;
  label?: string;
};

function _ApiKeyModal({
  handleHide,
  onSuccess,
  show: isVisible,
  provider,
  integration,
  label: _label,
  accountId,
}: Props) {
  const toast = useToast();
  const { clientId } = useParams<{ clientId: string }>();

  const { client } = useClientById(clientId, {
    accountFetchPolicy: "cache-first",
    clientFetchPolicy: "cache-first",
    skipFetchAssetsOnLoad: true,
    onlyFetchClient: true,
  });

  const timezone = client?.timezone || "UTC";

  const myToast = useMyToast();
  const [label, setLabel] = useState(_label || Helpers.capitalize(provider));
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [createAccount, { loading }] = useMutation(api.accounts.create);
  const [refreshApiKey, { loading: loadingRefreshApiKey }] = useMutation(
    api.accounts.refreshApiKey
  );
  const [startImportingAt, setStartImportingAt] = useState("");

  const formattedDate = useMemo(() => {
    if (!startImportingAt) return "";

    // move the date to UTC, and then add the timezone to it

    const d = moment.tz(startImportingAt, timezone);

    return d.toISOString();
  }, [startImportingAt, timezone]);

  const _onAddWallet = useCallback(async () => {
    try {
      if (!label || !apiKey || !secretKey) {
        return toast({
          position: "top",
          render: () => (
            <MyToast
              message={"Missing fields, please fill out below 👇"}
              status="error"
            />
          ),
        });
      }

      // add the offset to the date to get the UTC date

      const browserOffset = new Date().getTimezoneOffset();
      const offset = moment.tz.zone(timezone)?.utcOffset(Date.now()) || 0;
      const dateInClientTz = moment
        .utc(startImportingAt)
        .add(offset, "minutes")
        .subtract(Math.abs(browserOffset), "minutes");

      if (accountId) {
        const refreshApiKeyArgs: MutationRefreshApiKeyArgs = {
          accountId,
          clientId: clientId || "",
          apiKey,
          secretKey,
        };

        await refreshApiKey({
          variables: refreshApiKeyArgs,
          refetchQueries: [
            api.clients.accounts,
            api.portfolio.get,
            api.partnershipDiscounts.list,
          ],
        });

        myToast.show({
          message: `Account updated successfully!`,
          status: "success",
        });

        handleHide();

        if (onSuccess) {
          onSuccess();
        }

        return;
      }

      const createAccountArgs: MutationCreateAccountArgs = {
        code: null,
        provider: provider,
        type: AccountTypeEnum.Wallet,
        apiKey,
        secretKey,
        redirectUrl: null,
        address: null,
        importType: ImportTypeEnum.ApiKey,
        label: label ? `Kraken` : null,
        clientId: clientId || null,
        skipRecalculate: false,
        isLargeAccount: false,
        includeStakingRewards: false,
        shouldUploadAllEVM: false,
        startImportingAt: dateInClientTz.toDate(),
      };

      await createAccount({
        variables: createAccountArgs,
        refetchQueries: [
          api.clients.accounts,
          api.portfolio.get,
          api.partnershipDiscounts.list,
        ],
      });

      myToast.show({
        message: `Account created successfully!`,
        status: "success",
      });

      handleHide();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (err instanceof Error) {
        toast({
          position: "top",
          render: () => (
            <MyToast message={(err as Error).message} status="error" />
          ),
        });
      }
    }
  }, [apiKey, clientId, handleHide, label, onSuccess, provider, secretKey]);

  const theme = useTheme();
  const isLarge = useIsLargeScreen();

  return (
    <Modal
      title={
        <>
          <WalletHeaderImage
            logoUrl={
              integration && integration.logoUrl // `integration` is undefined sometimes for some reason
            }
          />
          Add Account
        </>
      }
      isVisible={isVisible}
      handleHide={handleHide}
      maxW="600px"
      w={isLarge ? undefined : "95%"}
      overflow="visible"
      Footer={
        <Box w="100%">
          <Button
            width="100%"
            variant="primary"
            isLoading={loading || loadingRefreshApiKey}
            onClick={_onAddWallet}
          >
            Add Account
          </Button>
        </Box>
      }
      headerProps={{
        style: {
          padding: isLarge ? "2rem 2rem 1rem 2rem" : "1rem",
        },
      }}
      footerProps={{
        style: {
          padding: isLarge ? "2rem" : "1rem",
        },
      }}
      bodyProps={{
        style: {
          padding: isLarge ? "0 2rem" : "1rem",
        },
      }}
    >
      <Container padding="0px" marginTop="25px !important" paddingBottom="2rem">
        {provider === "kraken" && (
          <HelpLink
            helpUrl={
              "https://help.awaken.tax/en/articles/11101871-setting-up-kraken"
            }
            description={
              <Text fontSize="sm" color={theme.text}>
                Step-by-step guide on how to set up your Kraken API key.
              </Text>
            }
          />
        )}

        <Input
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          label="API Key"
          placeholder={""}
        />

        <Input
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          label="Private Key"
          placeholder={""}
          type="password"
        />

        <Box mt={"20px"}>
          <Touchable
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconPosition="right"
            iconName="fa-sharp fa-chevron-down"
            label={"See Advanced Options"}
            bg={theme.medBackground}
          />

          <Collapse in={showAdvanced}>
            <Box mt={2} p={4} bg={theme.medBackground} borderRadius="md">
              <DatePicker
                containerStyle={{ marginBottom: 5 }}
                label="Import Transactions From Date"
                subtitle="Only import transactions after this date. If you have transactions before this date you'll need to make sure you add them!"
                infoMessage="If you set this date and are missing previous transactions, your tax history will be incomplete. If you choose to do this, make sure to address missing cost basis; we cannot help you with this."
                name="date"
                onChange={(date) => {
                  // Create a moment in the client's timezone from the selected date
                  // const dateInClientTz = moment.tz(date, timezone);
                  setStartImportingAt(date?.toISOString() || "");
                }}
                value={startImportingAt ? new Date(startImportingAt) : null}
                dateFormat="MM/dd/yyyy"
                showTimeSelect={false}
                placeholder=""
              />

              {formattedDate && (
                <>
                  <Text fontSize="sm" marginBottom="1.5rem" color={theme.text}>
                    Will import after{" "}
                    {moment(formattedDate).format("h:mma M/D/YYYY")} ({timezone}
                    ){" "}
                    <Info message="This is the time of the first transaction in your wallet. It's inclusive, so if you import from 12:00 AM, it will include transactions that happened at exactly 12:00 AM and onwards." />
                  </Text>
                </>
              )}
            </Box>
          </Collapse>
        </Box>
      </Container>
    </Modal>
  );
}

export const ApiKeyModal = connectModal({
  name: "ApiKeyModal",
})(_ApiKeyModal);
